// src/types/coordinateGrid.ts
/**
 * Type definitions for the 2D Coordinate Grid feature
 * Phase 1: Core infrastructure types
 */

/**
 * Basic 2D point representation
 */
export interface Point2D {
  x: number;
  y: number;
}

/**
 * Grid visual style options
 */
export type GridStyle = 'lines' | 'dots' | 'none';

/**
 * Grid density presets
 * - coarse: gridlines at integer intervals only
 * - normal: gridlines at 0.5 intervals
 * - fine: gridlines at 0.25 intervals
 */
export type GridDensity = 'coarse' | 'normal' | 'fine';

/**
 * Label format for axis tick labels
 */
export type AxisLabelFormat = 'integer' | 'decimal' | 'fraction';

/**
 * Quadrant visibility configuration
 * Each quadrant can be independently shown/hidden
 */
export interface QuadrantVisibility {
  /** Quadrant 1: +x, +y (top-right) */
  q1: boolean;
  /** Quadrant 2: -x, +y (top-left) */
  q2: boolean;
  /** Quadrant 3: -x, -y (bottom-left) */
  q3: boolean;
  /** Quadrant 4: +x, -y (bottom-right) */
  q4: boolean;
}

/**
 * Axis configuration for X and Y axes
 */
export interface AxisSettings {
  /** Show the X-axis line */
  showXAxis: boolean;
  /** Show the Y-axis line */
  showYAxis: boolean;
  /** Label for the X-axis (displayed at the end of the axis) */
  xLabel: string;
  /** Label for the Y-axis (displayed at the end of the axis) */
  yLabel: string;
  /** Show arrow heads at the positive ends of axes */
  showArrows: boolean;
  /** Interval between major tick marks */
  tickInterval: number;
  /** Format for tick labels */
  labelFormat: AxisLabelFormat;
  /** Show tick labels on X-axis */
  showXLabels: boolean;
  /** Show tick labels on Y-axis */
  showYLabels: boolean;
}

/**
 * Main coordinate grid state
 */
export interface CoordinateGridState {
  /** Whether to show the background grid */
  showGrid: boolean;
  /** Visual style of the grid (lines, dots, or none) */
  gridStyle: GridStyle;
  /** Density of gridlines */
  gridDensity: GridDensity;
  /** Color of the grid lines/dots */
  gridColor: string;
  /** Whether to show minor gridlines between major gridlines */
  showMinorGridlines: boolean;
  /** Minor gridline color (typically lighter than main grid) */
  minorGridColor: string;
  /** Axis display and labeling settings */
  axisSettings: AxisSettings;
  /** Which quadrants are visible */
  quadrantsVisible: QuadrantVisibility;
  /** Lock X and Y scales to be equal (maintains square grid cells) */
  equalScaling: boolean;
  /** Axis line color */
  axisColor: string;
  /** Axis line thickness */
  axisThickness: number;
  /** Origin marker visibility */
  showOriginMarker: boolean;
}

/**
 * Default axis settings
 */
export const DEFAULT_AXIS_SETTINGS: AxisSettings = {
  showXAxis: true,
  showYAxis: true,
  xLabel: 'x',
  yLabel: 'y',
  showArrows: true,
  tickInterval: 1,
  labelFormat: 'integer',
  showXLabels: true,
  showYLabels: true,
};

/**
 * Default quadrant visibility (all visible)
 */
export const DEFAULT_QUADRANT_VISIBILITY: QuadrantVisibility = {
  q1: true,
  q2: true,
  q3: true,
  q4: true,
};

/**
 * Default coordinate grid state
 */
export const DEFAULT_COORDINATE_GRID_STATE: CoordinateGridState = {
  showGrid: true,
  gridStyle: 'lines',
  gridDensity: 'normal',
  gridColor: '#E5E7EB',
  showMinorGridlines: false,
  minorGridColor: '#F3F4F6',
  axisSettings: DEFAULT_AXIS_SETTINGS,
  quadrantsVisible: DEFAULT_QUADRANT_VISIBILITY,
  equalScaling: true,
  axisColor: '#374151',
  axisThickness: 2,
  showOriginMarker: true,
};

/**
 * Quadrant presets for quick selection
 */
export type QuadrantPreset = 
  | 'all'           // All four quadrants
  | 'q1-only'       // First quadrant only (positive x and y)
  | 'q1-q4'         // Right half (positive x)
  | 'q1-q2'         // Top half (positive y)
  | 'custom';       // Custom selection

/**
 * Get quadrant visibility from preset
 */
export function getQuadrantPreset(preset: QuadrantPreset): QuadrantVisibility {
  switch (preset) {
    case 'all':
      return { q1: true, q2: true, q3: true, q4: true };
    case 'q1-only':
      return { q1: true, q2: false, q3: false, q4: false };
    case 'q1-q4':
      return { q1: true, q2: false, q3: false, q4: true };
    case 'q1-q2':
      return { q1: true, q2: true, q3: false, q4: false };
    case 'custom':
    default:
      return { q1: true, q2: true, q3: true, q4: true };
  }
}

/**
 * Detect which preset matches the current quadrant visibility
 */
export function detectQuadrantPreset(visibility: QuadrantVisibility): QuadrantPreset {
  const { q1, q2, q3, q4 } = visibility;
  
  if (q1 && q2 && q3 && q4) return 'all';
  if (q1 && !q2 && !q3 && !q4) return 'q1-only';
  if (q1 && !q2 && !q3 && q4) return 'q1-q4';
  if (q1 && q2 && !q3 && !q4) return 'q1-q2';
  return 'custom';
}

/**
 * Grid density to interval mapping
 */
export function getGridInterval(density: GridDensity): number {
  switch (density) {
    case 'coarse':
      return 1;
    case 'normal':
      return 0.5;
    case 'fine':
      return 0.25;
    default:
      return 0.5;
  }
}

/**
 * Get minor grid interval (half of main interval)
 */
export function getMinorGridInterval(density: GridDensity): number {
  return getGridInterval(density) / 2;
}
